import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useTranslator from "../../hooks/useTranslator";
import { getInitials } from "../../helper/applicationHelpers";
import SkorProtectShield from "../../assets/img/skorLogo.svg";
import Flag from "../../assets/img/flag.svg";
import DocumentIcon from "../../assets/img/docIcon02.svg";
import DownArrowIcon from "../../assets/img/down_arrow.svg";
import {
  ShieldIcon,
  MedalIcon,
  PeopleIcon,
  PersonIcon,
  DocSquareIcon,
  ClockIcon,
  InfoIcon,
  CheckIcon,
  LockIcon,
} from "./SkorProtectIcons";
import "./SkorProtectPayment.css";
import "./SkorProtectPaymentDetails.css";

const SKOR_PROTECT_WINDOW_HOURS = 48;

const STATUS = {
  LOADING: "loading",
  DETAILS: "details",
  REDIRECTING: "redirecting",
  SUCCESS: "success",
  FAILURE: "failure",
  ERROR: "error",
};

const TERM_ITEMS = [
  {
    titleKey: "skorIsYourBacking",
    bodyKey: "skorBacksYouWithTheLandlordAndGivesAccessToPrivilegedTerms",
    icon: <ShieldIcon size={14} />,
  },
  {
    titleKey: "legalAdvice",
    bodyKey: "questionsAboutYourRightsAsATenant",
    icon: <DocSquareIcon size={14} />,
  },
  {
    titleKey: "supportWithYourLandlord",
    bodyKey: "weHelpYouHandleIssuesWithYourLandlord",
    icon: <PersonIcon size={14} />,
  },
  {
    titleKey: "buildYourSkorHistory",
    bodyKey: "yourTenantTrackRecordIsLoggedMonthByMonth",
    icon: <ClockIcon size={14} />,
  },
];

// Short recap shown in the inline dropdown (matches the source HTML's "legal-body" accordion).
const LEGAL_SUMMARY = [
  { titleKey: "legalAgreementIntroTitle", bodyKey: "legalAgreementIntroBody" },
  { titleKey: "startOfCoverage", bodyKey: "startOfCoverageBody" },
  { bodyKey: "tenantsAreJointlyAndSeverallyLiableToSkor" },
  { titleKey: "termAndRenewal", bodyKey: "termAndRenewalBody" },
  {
    titleKey: "instalmentsAndAcceleratedMaturity",
    bodyKey: "instalmentsAndAcceleratedMaturityBody",
  },
  { titleKey: "rightOfWithdrawal", bodyKey: "rightOfWithdrawalBody" },
  {
    titleKey: "commitmentAndCancellation",
    bodyKey: "commitmentAndCancellationBody",
  },
  { titleKey: "backingAndDefault", bodyKey: "backingAndDefaultBody" },
  { titleKey: "limitationOfLiability", bodyKey: "limitationOfLiabilityBody" },
  { bodyKey: "summaryDoesntReplaceFullContractualDocument" },
];

// Full numbered clauses shown in the "Skor Protect Agreement" doc modal.
const CONTRACT_CLAUSES = [
  { titleKey: "natureOfAgreement", bodyKey: "natureOfAgreementBody" },
  { titleKey: "purposeOfAgreement", bodyKey: "purposeOfAgreementBody" },
  { titleKey: "startOfCoverage", bodyKey: "startOfCoverageBody" },
  { bodyKey: "tenantsAreJointlyAndSeverallyLiableToSkor" },
  { titleKey: "servicesProvided", bodyKey: "servicesProvidedBody" },
  { titleKey: "priceAndFees", bodyKey: "priceAndFeesBody" },
  { titleKey: "termAndRenewal", bodyKey: "termAndRenewalBody" },
  {
    titleKey: "instalmentsAndAcceleratedMaturity",
    bodyKey: "instalmentsAndAcceleratedMaturityBody",
  },
  { titleKey: "rightOfWithdrawal", bodyKey: "rightOfWithdrawalBody" },
  {
    titleKey: "commitmentAndCancellation",
    bodyKey: "commitmentAndCancellationBody",
  },
  { titleKey: "backingAndDefault", bodyKey: "backingAndDefaultBody" },
  { titleKey: "limitationOfLiability", bodyKey: "limitationOfLiabilityBody" },
];

const SEPA_ITEMS = [
  { labelKey: "paymentType", valueKey: "recurrentAnnualOrMonthlyCharge" },
  { labelKey: "yourRights", valueKey: "entitledToRefundFromYourBank" },
  {
    labelKey: "uniqueMandateReference",
    valueKey: "mandateReferenceAssignedBeforeFirstDebit",
  },
  {
    labelKey: "revocation",
    valueKey: "canRevokeMandateAnytimeByNotifyingBank",
  },
];

const ACK_KEYS = [
  "instalmentDefaultAuthorizesFullAnnualCharge",
  "canOnlyCancelIfTenancyEndsOrLandlordAuthorizes",
  "skorProtectDoesntExemptMeFromLandlordObligations",
  "ifIStopPayingRentSkorMayClaimAndReportIt",
];

const getTenantName = (tenant) =>
  [tenant?.fname, tenant?.lname].filter(Boolean).join(" ");

const formatCountdown = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
};

const CenterLayout = ({ children }) => (
  <div className="skp-page">
    <div className="skp-card">{children}</div>
  </div>
);

const SpinnerIcon = () => (
  <div className="skp-spinner-wrap">
    <svg
      className="skp-spinner"
      width="42"
      height="42"
      viewBox="0 0 42 42"
      fill="none"
    >
      <circle cx="21" cy="21" r="17" stroke="#e8eceb" strokeWidth="4" />
      <path
        d="M38 21a17 17 0 0 0-17-17"
        stroke="#253837"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const StatusIcon = ({ type }) => {
  const isSuccess = type === "success";

  return (
    <div className={`skp-status-icon ${isSuccess ? "success" : "error"}`}>
      {isSuccess ? (
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M10.5 17.5l5 5 9-10"
            stroke="#253837"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M11 11l12 12M23 11L11 23"
            stroke="#253837"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  );
};

const ActionButton = ({ children, onClick }) => (
  <button className="skp-btn" onClick={onClick}>
    {children}
  </button>
);

const SkorProtectPayment = () => {
  const { _t, i18n } = useTranslator();
  const navigate = useNavigate();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState(null);
  const [details, setDetails] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState(null);
  const [legalOpen, setLegalOpen] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [lang, setLang] = useState(
    i18n.language?.startsWith("en") ? "en-us" : "es"
  );

  const changeLang = (langVal) => {
    i18n.changeLanguage(langVal);
    setLang(langVal);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    const paymentStatus = params.get("status");

    window.history.replaceState({}, "", window.location.pathname);

    if (paymentStatus === "success") {
      setStatus(STATUS.SUCCESS);
      return;
    }

    if (paymentStatus === "failure") {
      setStatus(STATUS.FAILURE);
      return;
    }

    if (!urlToken) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("invalidOrExpiredPaymentLink"));
      return;
    }

    setToken(urlToken);
    fetchDetails(urlToken);
  }, []);

  useEffect(() => {
    if (status !== STATUS.DETAILS || !details?.skor_protect_initiated) {
      return undefined;
    }

    const deadlineMs =
      new Date(details.skor_protect_initiated).getTime() +
      SKOR_PROTECT_WINDOW_HOURS * 60 * 60 * 1000;

    const computeRemaining = () =>
      Math.max(0, Math.floor((deadlineMs - Date.now()) / 1000));

    setSecondsLeft(computeRemaining());
    const interval = setInterval(() => {
      setSecondsLeft(computeRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, [status, details]);

  const fetchDetails = async (tokenValue) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/skor-protect/details`,
        {
          params: { token: tokenValue },
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      );

      setDetails(res.data?.success_res || {});
      setStatus(STATUS.DETAILS);
    } catch (err) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
    }
  };

  const initiatePayment = async (tokenValue) => {
    setStatus(STATUS.REDIRECTING);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/skor-protect/initiate-payment`,
        { token: tokenValue },
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      );

      const paymentUrl = res.data?.success_res?.paymentUrl;

      if (!paymentUrl) {
        setStatus(STATUS.ERROR);
        setErrorMessage(_t("paymentLinkNotReceived"));
        return;
      }

      window.location.href = paymentUrl;
    } catch (err) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
    }
  };

  const handleContinueToPayment = () => {
    if (!consentChecked || !token) return;
    initiatePayment(token);
  };

  const goToProfile = () => {
    navigate("/home");
  };

  if (status === STATUS.LOADING) {
    return (
      <CenterLayout>
        <SpinnerIcon />

        <h2 className="skp-title">{_t("pleaseWait")}</h2>
      </CenterLayout>
    );
  }

  if (status === STATUS.REDIRECTING) {
    return (
      <CenterLayout>
        <SpinnerIcon />

        <h2 className="skp-title">{_t("redirectingToPayment")}</h2>

        <p className="skp-text">
          {_t("pleaseWaitWhileWeSecurelyProcessYourPayment")}
        </p>
      </CenterLayout>
    );
  }

  if (status === STATUS.SUCCESS) {
    return (
      <CenterLayout>
        <StatusIcon type="success" />

        <h2 className="skp-title">{_t("paymentSuccessful")}</h2>

        <p className="skp-text">
          {_t("yourPaymentHasBeenCompletedSuccessfully")}
        </p>

        <ActionButton onClick={goToProfile}>{_t("goToMyProfile")}</ActionButton>
      </CenterLayout>
    );
  }

  if (status === STATUS.FAILURE) {
    return (
      <CenterLayout>
        <StatusIcon type="error" />

        <h2 className="skp-title">{_t("paymentNotCompleted")}</h2>

        <p className="skp-text">
          {_t("yourPaymentCouldNotBeCompletedPleaseTryAgain")}
        </p>

        <ActionButton onClick={goToProfile}>{_t("goToMyProfile")}</ActionButton>
      </CenterLayout>
    );
  }

  if (status === STATUS.DETAILS) {
    const tenants = [...(details?.selected_tenants || [])].sort((a, b) => {
      const aIsPayer = String(a._id) === String(details?.tenant_id);
      const bIsPayer = String(b._id) === String(details?.tenant_id);
      return aIsPayer === bIsPayer ? 0 : aIsPayer ? -1 : 1;
    });

    return (
      <div className="skpd-page">
        <header className="skpd-header">
          <div className="skpd-topbar">
            <div className="skpd-brand">
              <div className="skpd-brand-mark">S</div>
              <div className="skpd-brand-word">SKOR</div>
              <div className="skpd-brand-sub">{_t("tenants")}</div>
            </div>
            {lang === "es" ? (
              <div
                className="language-switch-container language-switch-container-es cursor-pointer"
                onClick={() => changeLang("en-us")}
              >
                <p>ES</p>
                <img
                  className="flag-image"
                  alt="Flag"
                  src="/images/spain-flag.png"
                />
              </div>
            ) : (
              <div
                className="language-switch-container cursor-pointer"
                onClick={() => changeLang("es")}
              >
                <img className="flag-image" alt="Flag" src={Flag} />
                <p>EN</p>
              </div>
            )}
          </div>

          <div className="skpd-progress-wrap">
            <div className="skpd-progress">
              <div className="skpd-p-step">
                <div className="skpd-p-dot done">✓</div>
                <div className="skpd-p-label">{_t("approval")}</div>
              </div>
              <div className="skpd-p-line done" />
              <div className="skpd-p-step">
                <div className="skpd-p-dot now">2</div>
                <div className="skpd-p-label now">{_t("terms")}</div>
              </div>
              <div className="skpd-p-line" />
              <div className="skpd-p-step">
                <div className="skpd-p-dot next">3</div>
                <div className="skpd-p-label">{_t("payment")}</div>
              </div>
            </div>
          </div>

          {secondsLeft !== null && (
            <div className="skpd-deadline-banner">
              <span className="skpd-deadline-text">
                {_t("yourLandlordAcceptedYourSkorProtect")}
              </span>
              <span className="skpd-deadline-timer">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 3" />
                </svg>
                <strong>{formatCountdown(secondsLeft)}</strong>
              </span>
            </div>
          )}
        </header>

        <div className="skpd-wrap">
          <div className="skpd-hero">
            <span className="skpd-eyebrow">
              {_t("theLandlordHasAcceptedYou")}
            </span>
            <h1 className="skpd-h1">{_t("confirmYourSkorProtectSeal")}</h1>
            <p className="skpd-hero-p">
              <u>{_t("landlordReviewedYourProfileAndAcceptedSkorProtect")}</u>{" "}
              {_t("completeNowToKeepYourCandidacyActive")}
            </p>
          </div>

          <div className="skpd-card">
            <div className="skpd-card-head">
              <div className="skpd-card-icon skpd-icon-lime">
                <MedalIcon size={16} />
              </div>
              <div>
                <div className="skpd-card-title">
                  {_t("howSkorBacksYourLease")}
                </div>
                <div className="skpd-card-sub">
                  {_t("reassuranceOfHavingSomeoneVouchForYou")}
                </div>
              </div>
            </div>

            <p className="skpd-stamp-p">
              {_t("skorProtectIsYourSealOfQualityWithTheLandlord")}
            </p>

            <div className="skpd-lease-subhead">
              <div className="skpd-lease-subhead-title">
                {_t("skorProtectActivatesWhenLeaseIsSigned")}
              </div>
              <div className="skpd-lease-subhead-sub">
                {_t("skorBacksYouForTheWholeLifeOfYourLease")}
              </div>
            </div>
            <p className="skpd-stamp-p">
              {_t("ifYouDontSignTheLeaseWeRefundYouInFull")}
            </p>
          </div>

          {tenants.length > 0 && (
            <div className="skpd-card">
              <div className="skpd-card-head">
                <div className="skpd-card-icon skpd-icon-purple">
                  <PeopleIcon size={16} />
                </div>
                <div>
                  <div className="skpd-card-title">{_t("coveredTenants")}</div>
                  <div className="skpd-card-sub">
                    {_t("coveredByThisSamePayment")}
                  </div>
                </div>
              </div>

              <div className="skpd-tenant-note">
                <InfoIcon size={15} />
                <span>
                  {_t("paymentByAnyTenantActivatesCoverageForEveryone")}
                </span>
              </div>

              <div className="skpd-tenant-list">
                {tenants.map((tenant, idx) => {
                  const tenantName = getTenantName(tenant);
                  const isPayer =
                    details?.tenant_id &&
                    String(tenant._id) === String(details.tenant_id);

                  return (
                    <div className="skpd-tenant-item" key={tenant._id || idx}>
                      <div className="skpd-avatar">
                        {getInitials(tenantName)}
                      </div>
                      <div className="skpd-tenant-info">
                        <div className="skpd-tenant-name">{tenantName}</div>
                        {tenant.email && (
                          <div className="skpd-tenant-tag">{tenant.email}</div>
                        )}
                      </div>
                      {isPayer && (
                        <div className="skpd-badge-you">
                          {_t("youArePaying")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="skpd-card">
            <div className="skpd-card-head">
              <div className="skpd-card-icon skpd-icon-green">
                <ShieldIcon size={16} />
              </div>
              <div>
                <div className="skpd-card-title">
                  {_t("whatDoesSkorProtectInclude")}
                </div>
                <div className="skpd-card-sub">
                  {_t("skorBacksYouAsATenantForTheWholeLease")}
                </div>
              </div>
            </div>

            <div className="skpd-term-list">
              {TERM_ITEMS.map((term) => (
                <div className="skpd-term-item" key={term.titleKey}>
                  <div className="skpd-term-icon">{term.icon}</div>
                  <div>
                    <div className="skpd-term-h">{_t(term.titleKey)}</div>
                    <div className="skpd-term-p">{_t(term.bodyKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="skpd-card">
            <button
              type="button"
              className="skpd-legal-toggle"
              onClick={() => setLegalOpen((open) => !open)}
            >
              <img src={DocumentIcon} width="16" height="16" alt="" />
              <span className="skpd-legal-toggle-label">
                {_t("viewTheFullServiceAgreement")}
              </span>
              <img
                src={DownArrowIcon}
                width="12"
                height="7"
                alt=""
                className={`skpd-legal-chevron ${legalOpen ? "open" : ""}`}
              />
            </button>

            {legalOpen && (
              <div className="skpd-legal-body">
                {LEGAL_SUMMARY.map((clause, idx) => (
                  <p key={idx}>
                    {clause.titleKey && <strong>{_t(clause.titleKey)} </strong>}
                    {_t(clause.bodyKey)}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className="skpd-consent-card">
            <div className="skpd-consent-ack-list">
              {ACK_KEYS.map((key) => (
                <div className="skpd-ack-item" key={key}>
                  <CheckIcon size={14} />
                  <span>{_t(key)}</span>
                </div>
              ))}
            </div>

            <div
              className="skpd-consent-row"
              onClick={() =>
                setConsentChecked((checked) => {
                  const next = !checked;
                  setConsentTimestamp(next ? new Date().toISOString() : null);
                  return next;
                })
              }
            >
              <div
                className={`skpd-consent-checkbox ${consentChecked ? "checked" : ""}`}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#253837"
                  strokeWidth="3"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              <div className="skpd-consent-text">
                {_t("iHaveReadAndAcceptThe")}{" "}
                <button
                  type="button"
                  className="skpd-link-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDoc("contract");
                  }}
                >
                  {_t("skorProtectAgreement")}
                </button>{" "}
                {_t("andThe")}{" "}
                <button
                  type="button"
                  className="skpd-link-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDoc("sepa");
                  }}
                >
                  {_t("sepaDirectDebitMandate")}
                </button>
                , {_t("includingThePaymentAndSubrogationConditionsAbove")}
              </div>
            </div>

            {consentChecked && consentTimestamp && (
              <div className="skpd-consent-log">
                {_t("timestamp")}: {consentTimestamp}
              </div>
            )}
          </div>
        </div>

        <div className="skpd-footer-bar">
          <div className="skpd-footer-inner">
            {details?.skor_protect_amount != null && (
              <div className="skpd-footer-price">
                <div className="skpd-amt">
                  {Number(details.skor_protect_amount).toLocaleString("es-ES")}€
                </div>
                <div className="skpd-lbl">{_t("today")}</div>
              </div>
            )}
            <button
              type="button"
              className="skpd-cta-btn"
              disabled={!consentChecked}
              onClick={handleContinueToPayment}
            >
              <span className="skpd-icon-lock">
                <LockIcon size={14} />
              </span>
              <span>{_t("proceedWithPayment")}</span>
            </button>
          </div>
        </div>

        {openDoc && (
          <div
            className="skpd-overlay show"
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpenDoc(null);
            }}
          >
            <div className="skpd-overlay-card">
              <div className="skpd-overlay-card-head">
                <h3>
                  {openDoc === "contract"
                    ? _t("professionalServicesAgreement")
                    : _t("sepaDirectDebitMandate")}
                </h3>
              </div>
              <div className="skpd-overlay-meta">
                {openDoc === "contract" ? (
                  _t("madrentVenturesTaxIdVersion")
                ) : (
                  <>
                    {_t("creditor")}: MADRENT VENTURES, S.L. — {_t("taxId")}{" "}
                    B56523731
                  </>
                )}
              </div>
              <div className="skpd-overlay-body">
                {openDoc === "contract" && (
                  <>
                    {CONTRACT_CLAUSES.map((clause, idx) => (
                      <p key={idx}>
                        {clause.titleKey && (
                          <strong>{_t(clause.titleKey)} </strong>
                        )}
                        {_t(clause.bodyKey)}
                      </p>
                    ))}
                    <p className="skpd-overlay-note">
                      {_t("summaryDoesntReplaceFullContractualDocument")}
                    </p>
                  </>
                )}
                {openDoc === "sepa" && (
                  <>
                    <p>{_t("sepaMandateIntro")}</p>
                    <p>
                      <strong>{_t("creditor")} IBAN:</strong> ES80 0182 0901
                      6102 0239 0189 — BBVA
                    </p>
                    {SEPA_ITEMS.map((item, idx) => (
                      <p key={idx}>
                        <strong>{_t(item.labelKey)}:</strong>{" "}
                        {_t(item.valueKey)}
                      </p>
                    ))}
                  </>
                )}
              </div>
              <button
                type="button"
                className="skpd-overlay-btn"
                onClick={() => setOpenDoc(null)}
              >
                {_t("close")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <CenterLayout>
      <img className="skp-logo" src={SkorProtectShield} alt="Skor Protect" />

      <h2 className="skp-title">{_t("unableToProcessPayment")}</h2>

      <p className="skp-text skp-error-text">{errorMessage}</p>

      <ActionButton onClick={goToProfile}>{_t("goToMyProfile")}</ActionButton>
    </CenterLayout>
  );
};

export default SkorProtectPayment;
